import type { ApiClient } from '@klareospace/pharos-lib-front-utils';
import { toQueryParams, mapPaginatedResponse } from '@klareospace/pharos-lib-front-utils';
import type {
  PaginatedResponse,
  PaginationParams,
  PharosApiResponse,
  PharosPaginatedResponse,
  PhoneNumber,
  SortParams,
  User,
  UserScope,
  UserStatus,
  UserWithResolvedScopes,
} from '@klareospace/pharos-lib-types';
import { USER_STATUS } from '@klareospace/pharos-lib-types';
import type { UserSortableColumn } from '../types/user.types';

// ─── Params ───────────────────────────────────────────────────────────────────

export interface SearchUsersParams extends PaginationParams {
  allowedDefaultVehicleId?: string;
  hasNoDefaultVehicle?: boolean;
  roles?: string[];
  scopes?: string[];
  siteId?: string;
  sort?: SortParams<UserSortableColumn>;
  spaceId?: string;
  statuses?: UserStatus[];
  text?: string;
}

export interface UpdateUserData {
  driverAttributes?: { carrierId: string; defaultVehicleId?: string | null } | null;
  firstName?: string;
  lastName?: string;
  phone?: PhoneNumber | null;
  roles?: string[];
  scopes?: UserScope[];
  status?: UserStatus;
}

// ─── API Factory ──────────────────────────────────────────────────────────────

export function createUserApi(client: ApiClient): {
  cancelInvitation: (id: string) => Promise<User>;
  deactivateUser: (id: string) => Promise<User>;
  getUser: (id: string) => Promise<UserWithResolvedScopes>;
  reactivateUser: (id: string) => Promise<User>;
  removeUser: (id: string) => Promise<User | null>;
  searchUsers: (params: SearchUsersParams) => Promise<PaginatedResponse<UserWithResolvedScopes>>;
  updateUser: (id: string, data: UpdateUserData) => Promise<UserWithResolvedScopes>;
} {
  return {
    cancelInvitation: async (id: string): Promise<User> => {
      const response = await client.execute<PharosApiResponse<User>>(() =>
        client.instance.post(`/users/${id}/cancel-invitation`),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    deactivateUser: async (id: string): Promise<User> => {
      const response = await client.execute<PharosApiResponse<User>>(() =>
        client.instance.patch(`/users/${id}`, { status: USER_STATUS.INACTIVE }),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    getUser: async (id: string): Promise<UserWithResolvedScopes> => {
      const response = await client.execute<PharosApiResponse<UserWithResolvedScopes>>(() =>
        client.instance.get(`/users/${id}`),
      );
      if (!response.data) {
        throw new Error('getUser: unexpected empty response');
      }
      return response.data;
    },

    reactivateUser: async (id: string): Promise<User> => {
      const response = await client.execute<PharosApiResponse<User>>(() =>
        client.instance.patch(`/users/${id}`, { status: USER_STATUS.ACTIVE }),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    removeUser: async (id: string): Promise<User | null> => {
      const response = await client.execute<PharosApiResponse<User | null>>(() =>
        client.instance.delete(`/users/${id}`),
      );
      return response.data ?? null;
    },

    searchUsers: async (params: SearchUsersParams): Promise<PaginatedResponse<UserWithResolvedScopes>> => {
      const body = toSearchBody(params);
      const query = toQueryParams(params);
      const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<UserWithResolvedScopes>>>(() =>
        client.instance.post('/users/search', body, { params: query }),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return mapPaginatedResponse(response.data);
    },

    updateUser: async (id: string, data: UpdateUserData): Promise<UserWithResolvedScopes> => {
      const response = await client.execute<PharosApiResponse<UserWithResolvedScopes>>(() =>
        client.instance.patch(`/users/${id}`, data),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Build the POST body for /users/search from filter params.
 * Only includes fields that have been explicitly set.
 */
function toSearchBody(params: SearchUsersParams): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const trimmedSearch = params.text?.trim();

  if (trimmedSearch) body.text = trimmedSearch;
  if (hasValues(params.roles)) body.roles = params.roles;

  applyScopeAndSpace(body, params);

  if (params.siteId) body.siteId = params.siteId;
  if (hasValues(params.statuses)) body.statuses = params.statuses;
  if (params.hasNoDefaultVehicle) body.hasNoDefaultVehicle = true;
  if (params.allowedDefaultVehicleId) body.allowedDefaultVehicleId = params.allowedDefaultVehicleId;

  return body;
}

// Explicit `scopes` filter combined with spaceId: prepend spaceId so space-scoped users
// (SPACE_ADMIN, CARRIER) remain visible alongside the explicitly scoped ones.
// Otherwise, a siteId request omits spaceId entirely — the backend already expands siteId to
// [siteId, parentSpaceId] itself, so re-sending spaceId would just expand the filter back out
// to the whole space.
function applyScopeAndSpace(body: Record<string, unknown>, params: SearchUsersParams): void {
  if (hasValues(params.scopes) && params.spaceId) {
    body.scopes = [params.spaceId, ...params.scopes];
    return;
  }
  if (hasValues(params.scopes)) body.scopes = params.scopes;
  if (params.spaceId && !params.siteId) body.spaceId = params.spaceId;
}

function hasValues(arr: unknown[] | undefined): arr is unknown[] {
  return !!arr && arr.length > 0;
}
